/**
 * Validates a federated logout redirect target.
 *
 * Intentionally allows any absolute `https:` URL (no trusted-domain allowlist).
 * Federated partners pass `redirectUrl` / `returnUrl` and we redirect to it after
 * sign-out. Relative same-origin paths are also allowed; non-HTTPS schemes and
 * protocol-relative URLs (`//…`) are rejected.
 *
 * @param {string | null | undefined} logoutUrl
 * @returns {string | null} Safe redirect target or null when invalid.
 */
export function resolveSafeLogoutRedirect(logoutUrl) {
  if (!logoutUrl || typeof logoutUrl !== 'string') return null

  const trimmedUrl = logoutUrl.trim()
  if (!trimmedUrl) return null

  // Allow in-app relative redirects but block protocol-relative URLs.
  if (trimmedUrl.startsWith('/')) {
    return trimmedUrl.startsWith('//') ? null : trimmedUrl
  }

  try {
    const parsed = new URL(trimmedUrl)
    // Any https host is accepted — we do not maintain a domain allowlist.
    return parsed.protocol === 'https:' ? parsed.toString() : null
  } catch {
    return null
  }
}

/**
 * Reads federated logout return URL from /sign-out query params.
 * Supports `returnUrl` and `return_url`; `returnUrl` wins when both are present.
 *
 * @param {URLSearchParams | string | null | undefined} searchParams
 * @returns {string | null} Raw return URL from query params, if present.
 */
export function resolveSignOutReturnUrl(searchParams) {
  const params =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(typeof searchParams === 'string' ? searchParams : '')

  const returnUrl = params.get('returnUrl')?.trim() || params.get('returnurl')?.trim()
  if (returnUrl) {
    return returnUrl || null
  }

  const legacyReturnUrl = params.get('return_url')?.trim()
  return legacyReturnUrl || null
}

/**
 * @param {URLSearchParams | string | null | undefined} searchParams
 * @returns {string | null} Validated return URL for post-sign-out redirect.
 */
export function resolveSafeSignOutReturnUrl(searchParams) {
  return resolveSafeLogoutRedirect(resolveSignOutReturnUrl(searchParams))
}

export function assignSignOutReturnUrl(returnUrl) {
  window.location.assign(returnUrl)
}
